import { Autocomplete as BaseAutocomplete } from '@base-ui/react/autocomplete'
import styled from 'styled-components'
import { Search } from 'sutra/components/Icons'
import { Input } from 'sutra/components/Input/Input'
import { accents, brand, gray } from 'sutra/tokens/colors'
import { motion } from 'sutra/tokens/motion'
import { radii } from 'sutra/tokens/radii'

// The popup, list and option rules mirror Select's so the two read as siblings.

export const InputWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

export const SearchIcon = styled(Search)`
  position: absolute;
  left: 12px;
  color: ${gray[500]};
  pointer-events: none;
`

export const FieldInput = styled(Input)<{
  $searchIcon: boolean
  $clearable: boolean
}>`
  ${({ $searchIcon }) => $searchIcon && 'padding-left: 36px;'}
  ${({ $clearable }) => $clearable && 'padding-right: 36px;'}
`

export const ClearButton = styled(BaseAutocomplete.Clear)`
  position: absolute;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: ${radii.xs}px;
  background: transparent;
  color: ${gray[500]};
  cursor: pointer;
  transition: background-color ${motion.duration.fast}ms ${motion.ease};

  &:hover {
    background: ${gray[200]};
    color: ${brand.stillNight[800]};
  }

  &:focus-visible {
    outline: 2px solid ${brand.stillNight[800]};
    outline-offset: 1px;
  }
`

export const Positioner = styled(BaseAutocomplete.Positioner)<{
  $zIndex: number
}>`
  z-index: ${({ $zIndex }) => $zIndex};
`

export const Popup = styled(BaseAutocomplete.Popup)`
  box-sizing: border-box;
  width: var(--anchor-width);
  max-width: var(--available-width);
  padding: 4px;
  background: ${brand.white};
  border: 1px solid ${gray[200]};
  border-radius: ${radii.sm}px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  outline: none;
  opacity: 1;
  transform: translateY(0);
  transition:
    opacity ${motion.duration.fast}ms ${motion.ease},
    transform ${motion.duration.fast}ms ${motion.ease};

  &[data-starting-style],
  &[data-ending-style] {
    opacity: 0;
    transform: translateY(-4px);
  }
`

export const List = styled(BaseAutocomplete.List)`
  margin: 0;
  padding: 0;
  max-height: min(280px, var(--available-height));
  overflow-y: auto;
  overscroll-behavior: contain;
  outline: none;
`

export const Item = styled(BaseAutocomplete.Item)`
  display: flex;
  align-items: center;
  padding: 8px 10px;
  border-radius: ${radii.xs}px;
  font-size: 14px;
  color: ${brand.stillNight[800]};
  cursor: pointer;
  user-select: none;
  outline: none;

  &[data-highlighted] {
    background: ${gray[200]};
  }
`

export const Empty = styled(BaseAutocomplete.Empty)`
  padding: 8px 10px;
  font-size: 14px;
  color: ${gray[400]};

  &:empty {
    display: none;
  }
`

// The app's global antd reset gives `mark` padding and a yellow background, so
// both are cleared here; padding left in would open a gap inside the word.
export const Match = styled.mark`
  margin: 0;
  padding: 0;
  background: transparent;
  color: ${accents.reflectionBlue[600]};
`
